import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fromPrice, inr, media } from "@/lib/format";
import { useKiosk } from "./KioskContext";

const QUESTIONS = [
  { key: "dist", q: "q1", opts: ["d1", "d2", "d3"] },
  { key: "budget", q: "q2", opts: ["b1", "b2", "b3"] },
  { key: "pt", q: "q3", opts: ["petrol", "ev", "noPref"] },
];
const BUDGET = { b1: [0, 100000], b2: [100000, 150000], b3: [150000, 1e9] };
const DAILY = { d1: 15, d2: 35, d3: 70 };

export const scoreModels = (models, a) =>
  models.map((m) => {
    const p = fromPrice(m) || 0;
    const [lo, hi] = BUDGET[a.budget];
    let s = p >= lo && p < hi ? 4 : Math.abs(p - (p < lo ? lo : hi)) < 20000 ? 1 : -2;
    const daily = DAILY[a.dist];
    if (m.powertrain === "EV") s += m.specs.range_km >= daily * 2.5 ? 2 : -3;
    else s += a.dist === "d3" && m.specs.mileage_kmpl >= 55 ? 2 : 1;
    if (a.pt === "petrol") s += m.powertrain === "ICE" ? 3 : -5;
    if (a.pt === "ev") s += m.powertrain === "EV" ? 3 : -5;
    return { m, s };
  }).sort((x, y) => y.s - x.s || (fromPrice(x.m) - fromPrice(y.m))).slice(0, 3).map((x) => x.m);

const Option = ({ label, onClick, testid }) => (
  <motion.button whileTap={{ scale: 0.97 }} onClick={onClick} data-testid={testid}
    className="tile min-h-[120px] p-7 text-left font-display text-2xl pk:!min-h-[180px] pk:!text-4xl font-bold tracking-[-0.02em] transition-colors hover:bg-[#ECECEF]">{label}</motion.button>
);

export default function Quiz() {
  const { data, t, logEvent } = useKiosk();
  const [answers, setAnswers] = useState({});
  const step = QUESTIONS.findIndex((q) => !answers[q.key]);
  const results = useMemo(() => (step === -1 ? scoreModels(data.models, answers) : []), [step, answers, data.models]);
  useEffect(() => { if (step === -1) logEvent("quiz", results[0]?.slug); }, [step, results, logEvent]);

  return (
    <main className="mx-auto max-w-[1100px] px-6 pb-12 pt-6 lg:px-10 pk:!pt-16" data-testid="quiz-page">
      <p className="text-[17px] font-semibold text-[#087568]">{t("findRide")}{step >= 0 ? ` · ${step + 1}/3` : ""}</p>
      {step >= 0 ? (
        <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="font-display mt-2 break-words text-4xl lg:text-[64px] font-extrabold leading-none tracking-[-0.045em]" data-testid="quiz-question">{t(QUESTIONS[step].q)}</h1>
          <div className="mt-10 grid gap-4 sm:grid-cols-3 pk:!grid-cols-1">
            {QUESTIONS[step].opts.map((o) => <Option key={o} label={t(o)} testid={`quiz-opt-${o}`} onClick={() => setAnswers({ ...answers, [QUESTIONS[step].key]: o })} />)}
          </div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h1 className="font-display mt-2 text-4xl lg:text-[64px] font-extrabold leading-none tracking-[-0.045em]">{t("matches")}</h1>
          <div className="mt-8 grid gap-5 md:grid-cols-3" data-testid="quiz-results">
            {results.map((m, i) => (
              <Link key={m.slug} to={`/kiosk/model/${m.slug}`} className="tile flex flex-col p-6 transition-transform active:scale-[0.98]" data-testid={`quiz-result-${m.slug}`}>
                {i === 0 && <span className="self-start rounded-full bg-[#0B8577] px-3 py-1 text-[12px] font-semibold text-white">#1</span>}
                <div className="relative mt-2 h-[170px]"><div className="floor-glow absolute bottom-0 left-1/2 h-12 w-[70%] -translate-x-1/2" /><img src={media(m.hero_image)} alt={m.name} className="product-shadow relative h-full w-full object-contain" /></div>
                <p className="mt-3 text-[14px] font-semibold text-[#087568]">{m.category}</p>
                <p className="font-display text-3xl font-extrabold tracking-[-0.03em]">{m.short}</p>
                <p className="text-[15px]">{t("from")} <b className="tnum">{inr(fromPrice(m))}</b></p>
                <p className="text-[12px] text-[#6E6E73]">{t("exShowroom")} · {t("indicative")}</p>
              </Link>
            ))}
          </div>
          <button onClick={() => setAnswers({})} className="btn-pill btn-ghost mt-8" data-testid="quiz-restart">{t("startOver")}</button>
        </motion.div>
      )}
    </main>
  );
}
